/**
 * GA4 + Meta Pixel Analytics Utility
 * ────────────────────────────────────
 * Typed wrappers around window.gtag (GA4) and window.fbq (Meta Pixel)
 * for firing conversion events.
 *
 * GA4 Measurement ID : G-6B2QBNVF2X
 * Meta Pixel ID      : 1548398680004440
 *
 * To mark a GA4 event as a conversion:
 *   GA4 → Admin → Events → find the event name → toggle "Mark as conversion"
 *
 * Meta Pixel standard events fire automatically alongside every GA4 lead event.
 * Verify in Meta Events Manager → Test Events tab after publishing.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

/** Fire a GA4 event safely (no-op if gtag is not loaded) */
function gtagEvent(eventName: string, params: Record<string, unknown> = {}) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

/** Push an event to GTM dataLayer (no-op if dataLayer is not available) */
function dlPush(eventName: string, params: Record<string, unknown> = {}) {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
  }
}

/**
 * Fire a Meta Pixel standard event safely (no-op if fbq is not loaded).
 * @param eventName  Meta standard event name e.g. 'Lead', 'Schedule'
 * @param params     Optional Meta event parameters (content_name, value, currency, etc.)
 */
function fbqTrack(eventName: string, params: Record<string, unknown> = {}) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("track", eventName, params);
  }
}

/**
 * Fire a Meta Pixel custom event safely (no-op if fbq is not loaded).
 * @param eventName  Custom event name e.g. 'BookCallClick'
 * @param params     Optional parameters
 */
function fbqTrackCustom(
  eventName: string,
  params: Record<string, unknown> = {}
) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq("trackCustom", eventName, params);
  }
}

// ─── Conversion events ────────────────────────────────────────────────────────

/**
 * Fired when the Free AI Practice Growth Report form is successfully submitted.
 * GA4 conversion: "free_report_submitted"
 * Meta Pixel: Lead (content_name: "Free AI Practice Growth Report")
 */
export function trackFreeReportSubmit(params?: {
  practiceType?: string;
  source?: string;
}) {
  const eventParams = {
    event_category: "lead_generation",
    event_label: "Free AI Practice Growth Report",
    practice_type: params?.practiceType ?? "unknown",
    source: params?.source ?? "website",
  };
  gtagEvent("free_report_submitted", eventParams);
  dlPush("free_report_submitted", eventParams);

  // Meta Pixel — Lead event
  fbqTrack("Lead", {
    content_name: "Free AI Practice Growth Report",
    content_category: "lead_generation",
    practice_type: params?.practiceType ?? "unknown",
  });
}

/**
 * Fired when the Book a Call / Strategy Session form is successfully submitted.
 * GA4 conversion: "book_call_submitted"
 * Meta Pixel: Schedule (content_name: "Strategy Session")
 */
export function trackBookCallSubmit(params?: {
  practiceType?: string;
  timeSlot?: string;
}) {
  const eventParams = {
    event_category: "lead_generation",
    event_label: "Strategy Session Booked",
    practice_type: params?.practiceType ?? "unknown",
    time_slot: params?.timeSlot ?? "unknown",
  };
  gtagEvent("book_call_submitted", eventParams);
  dlPush("book_call_submitted", eventParams);

  // Meta Pixel — Schedule event (native Meta event for booked appointments)
  fbqTrack("Schedule", {
    content_name: "Strategy Session",
    content_category: "booking",
    practice_type: params?.practiceType ?? "unknown",
  });
}

// ─── Engagement events ────────────────────────────────────────────────────────

/**
 * Fired when a "Book a Call" CTA button is clicked (before form completion).
 * Useful for funnel drop-off analysis.
 */
export function trackBookCallClick(params?: { location?: string }) {
  gtagEvent("book_call_click", {
    event_category: "engagement",
    event_label: params?.location ?? "unknown",
  });
  fbqTrackCustom("BookCallClick", { location: params?.location ?? "unknown" });
}

/**
 * Fired when a "Claim Free Report" CTA button is clicked.
 */
export function trackFreeReportClick(params?: { location?: string }) {
  gtagEvent("free_report_click", {
    event_category: "engagement",
    event_label: params?.location ?? "unknown",
  });
  fbqTrackCustom("FreeReportClick", {
    location: params?.location ?? "unknown",
  });
}

/**
 * Fired when a public phone number link is clicked. No visitor phone number is
 * collected; the location identifies where the public contact action occurred.
 */
export function trackPhoneClick(params?: { location?: string }) {
  const eventParams = {
    event_category: "contact",
    event_label: "Phone number clicked",
    link_location: params?.location ?? "unknown",
  };
  dlPush("phone_click", eventParams);
  fbqTrackCustom("PhoneClick", { location: eventParams.link_location });
}

/**
 * Fired when a public email address link is clicked. The event records only
 * the page location, never the visitor's email address or mailto recipient.
 */
export function trackEmailClick(params?: { location?: string }) {
  const eventParams = {
    event_category: "contact",
    event_label: "Email address clicked",
    link_location: params?.location ?? "unknown",
  };
  dlPush("email_click", eventParams);
  fbqTrackCustom("EmailClick", { location: eventParams.link_location });
}

/**
 * Fired when an email-only Growth Brief handoff starts. This is an engagement
 * event, not a lead conversion: the full request is counted only after its
 * details are successfully stored by trackFreeReportSubmit.
 */
export function trackGrowthBriefStart(params?: { location?: string }) {
  const eventParams = {
    event_category: "engagement",
    event_label: "Growth Brief started",
    link_location: params?.location ?? "unknown",
  };
  gtagEvent("growth_brief_started", eventParams);
  dlPush("growth_brief_started", eventParams);
}

/**
 * Fired only after the SEO audit request is successfully stored.
 */
export function trackSeoAuditSubmit(params?: { practiceType?: string }) {
  const eventParams = {
    event_category: "lead_generation",
    event_label: "SEO audit request",
    practice_type: params?.practiceType ?? "unknown",
  };
  gtagEvent("seo_audit_submitted", eventParams);
  dlPush("seo_audit_submitted", eventParams);
  fbqTrack("Lead", {
    content_name: "SEO Audit Request",
    content_category: "lead_generation",
    practice_type: eventParams.practice_type,
  });
}

/**
 * Fired only after a Practice Visibility request has been stored. This event
 * is sent directly to the existing GA4 configuration; it is not mapped to a
 * Google Ads conversion action unless and until a separate approved mapping is
 * configured.
 */
export function trackPracticeVisibilityRequest(params?: {
  practiceType?: string;
  source?: string;
}) {
  const eventParams = {
    event_category: "lead_generation",
    event_label: "Practice Visibility request",
    practice_type: params?.practiceType ?? "unknown",
    source: params?.source ?? "website",
  };
  gtagEvent("practice_visibility_requested", eventParams);
  fbqTrack("Lead", {
    content_name: "Practice Visibility Request",
    content_category: "lead_generation",
    practice_type: eventParams.practice_type,
  });
}

/**
 * Fired after a persisted Practice Visibility scan record is created. It does
 * not include the practice name, address, public ID, cookie secret or any PII.
 */
export function trackPracticeVisibilityScanStarted(params: { source: "google_place" | "manual" }) {
  const eventParams = {
    event_category: "engagement",
    source_type: params.source,
  };
  gtagEvent("practice_visibility_scan_started", eventParams);
  dlPush("practice_visibility_scan_started", eventParams);
}

/** Fired only after the private-report contact details have been stored. */
export function trackPracticeVisibilityUnlock() {
  const eventParams = { event_category: "lead_generation", source: "practice_visibility" };
  gtagEvent("practice_visibility_report_unlocked", eventParams);
  dlPush("practice_visibility_report_unlocked", eventParams);
}

/** Fired when a visitor opens a valid private report token route; no token is sent. */
export function trackPracticeVisibilityReportOpened() {
  const eventParams = { event_category: "engagement", source: "practice_visibility" };
  gtagEvent("practice_visibility_report_opened", eventParams);
  dlPush("practice_visibility_report_opened", eventParams);
}

/** Phase 3 private-report events. None include the report token or contact data. */
export function trackPracticeVisibilityReportView() {
  const eventParams = { event_category: "engagement", source: "practice_visibility" };
  gtagEvent("report_view", eventParams);
  dlPush("report_view", eventParams);
}

export function trackPracticeVisibilityPdfDownload() {
  const eventParams = { event_category: "engagement", source: "practice_visibility" };
  gtagEvent("pdf_download", eventParams);
  dlPush("pdf_download", eventParams);
}

export function trackPracticeVisibilityEmailCopy() {
  const eventParams = { event_category: "engagement", source: "practice_visibility" };
  gtagEvent("email_copy", eventParams);
  dlPush("email_copy", eventParams);
}

export function trackPracticeVisibilityChatRequest() {
  const eventParams = { event_category: "lead_generation", source: "practice_visibility" };
  gtagEvent("chat_request", eventParams);
  dlPush("chat_request", eventParams);
}

export function trackPracticeVisibilityServiceClick(serviceName: string) {
  const eventParams = { event_category: "engagement", source: "practice_visibility", service_name: serviceName };
  gtagEvent("service_click", eventParams);
  dlPush("service_click", eventParams);
}

/**
 * Fired when the contact form on /contact, ContactSection, or Footer is submitted.
 * GA4 conversion: "contact_form_submitted"
 * Meta Pixel: Lead (content_name: "Contact Form")
 */
export function trackContactFormSubmit() {
  gtagEvent("contact_form_submitted", {
    event_category: "lead_generation",
    event_label: "Contact Form",
  });
  dlPush("contact_form_submitted", { event_category: "lead_generation" });

  // Meta Pixel — Lead event
  fbqTrack("Lead", {
    content_name: "Contact Form",
    content_category: "lead_generation",
  });
}

/**
 * Fired when a solution/package enquiry is successfully submitted.
 * GA4 conversion: "package_enquiry_submitted"
 * Meta Pixel: Lead (content_name: solution name, value: estimated monthly retainer)
 */
export function trackPackageEnquirySubmit(params?: {
  solutionName?: string;
  tierLabel?: string;
  value?: number;
}) {
  gtagEvent("package_enquiry_submitted", {
    event_category: "lead_generation",
    event_label: params?.solutionName ?? "Package enquiry",
    tier: params?.tierLabel ?? "unknown",
    currency: "AUD",
    value: params?.value ?? 0,
  });

  // Meta Pixel — Lead event with value signal
  fbqTrack("Lead", {
    content_name: params?.solutionName ?? "Package Enquiry",
    content_category: "lead_generation",
    currency: "AUD",
    value: params?.value ?? 0,
  });
}

/**
 * Fired when the AI chat widget captures a lead (email submitted in chat).
 * GA4 conversion: "chat_lead_captured"
 * Meta Pixel: Lead (content_name: "Chat Widget")
 */
export function trackChatLeadCaptured(params?: { page?: string }) {
  gtagEvent("chat_lead_captured", {
    event_category: "lead_generation",
    event_label: "Chat widget lead",
    page: params?.page ?? "unknown",
  });

  // Meta Pixel — Lead event
  fbqTrack("Lead", {
    content_name: "Chat Widget",
    content_category: "lead_generation",
    page: params?.page ?? "unknown",
  });
}

/**
 * Fired when the Google Ads LP (/lp/google-ads) form is successfully submitted.
 * GA4 conversions: "google_ads_lp_lead" + "generate_lead"
 * Meta Pixel: Lead (content_name: "Google Ads LP", value: estimated ad spend)
 */
export function trackGoogleAdsLpLead(params?: {
  practiceType?: string;
  plan?: string;
  adSpend?: number;
}) {
  const eventParams = {
    event_category: "lead_generation",
    event_label: "Google Ads LP — Half-Price Offer",
    practice_type: params?.practiceType ?? "unknown",
    selected_plan: params?.plan ?? "unknown",
    currency: "AUD",
    value: params?.adSpend ?? 600,
  };

  // Primary named event — mark this as conversion in GA4
  gtagEvent("google_ads_lp_lead", eventParams);
  dlPush("google_ads_lp_lead", eventParams);

  // Secondary: native GA4 / Google Ads generate_lead event for Smart Bidding
  gtagEvent("generate_lead", {
    event_category: "lead_generation",
    event_label: "Google Ads LP",
    currency: "AUD",
    value: params?.adSpend ?? 600,
  });
  dlPush("generate_lead", {
    event_category: "lead_generation",
    currency: "AUD",
    value: params?.adSpend ?? 600,
  });

  // Meta Pixel — Lead event with value signal
  fbqTrack("Lead", {
    content_name: "Google Ads LP",
    content_category: "lead_generation",
    currency: "AUD",
    value: params?.adSpend ?? 600,
    practice_type: params?.practiceType ?? "unknown",
  });
}

/**
 * Fired when a user clicks any CTA on the Google Ads LP (before form submit).
 */
export function trackGoogleAdsLpCtaClick(params?: { location?: string }) {
  const eventParams = {
    event_category: "engagement",
    event_label: params?.location ?? "unknown",
  };
  gtagEvent("google_ads_lp_cta_click", eventParams);
  dlPush("google_ads_lp_cta_click", eventParams);
  fbqTrackCustom("LpCtaClick", { location: params?.location ?? "unknown" });
}

/**
 * Fired on successful Google Ads LP form submission to report a conversion
 * directly to Google Ads (bypasses GA4 import lag).
 */
export function trackGoogleAdsConversion(params: {
  sendTo: string;
  value?: number;
  currency?: string;
}) {
  const { sendTo, value = 600, currency = "AUD" } = params;

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: sendTo,
      value,
      currency,
    });
  }

  dlPush("google_ads_conversion", { send_to: sendTo, value, currency });
}

/**
 * Generic SPA page-view event for client-side route changes.
 * GA4's base config tag only fires the initial page_view; this re-sends it on
 * each in-app navigation so single-page route changes are counted.
 */
export function trackPageView(path: string, title?: string) {
  gtagEvent("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title:
      title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
}
